import React, {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

/**
 * Context value for Dynamic Attribute (DA) form configuration.
 * Provides DA-specific settings to child components without prop drilling.
 */
interface DaFormContextValue {
  /**
   * Whether the current form is a Dynamic Attribute form.
   */
  isDaForm: boolean;

  /**
   * Whether HTML tags should be rendered in labels.
   * Only enabled for DA forms.
   */
  enableHtmlLabels: boolean;
}

/**
 * Default context value when no provider exists.
 * This ensures FieldLabel works safely outside MetadataForm.
 */
const defaultContextValue: DaFormContextValue = {
  isDaForm: false,
  enableHtmlLabels: false,
};

const DaFormContext = createContext<DaFormContextValue>(defaultContextValue);

/**
 * Hook to access DA form context.
 * Returns default values when used outside of DaFormProvider (e.g., in FormBuilder canvas).
 *
 * @returns DaFormContextValue with isDaForm and enableHtmlLabels flags
 *
 * @example
 * // Inside a component rendered within MetadataForm
 * const { enableHtmlLabels } = useDaFormContext();
 * if (enableHtmlLabels) {
 *   // Render HTML in labels
 * }
 */
export const useDaFormContext = (): DaFormContextValue =>
  useContext(DaFormContext);

interface DaFormProviderProps {
  /**
   * Whether this is a Dynamic Attribute form.
   */
  isDaForm: boolean;

  /**
   * Whether to enable HTML rendering in labels.
   */
  enableHtmlLabels: boolean;

  /**
   * Child components to render within the provider.
   */
  children: ReactNode;
}

/**
 * Provider component for DA form context.
 * Wraps MetadataForm content to provide DA-specific configuration to child components.
 *
 * @example
 * <DaFormProvider isDaForm={true} enableHtmlLabels={true}>
 *   <PreviewFields />
 * </DaFormProvider>
 */
export function DaFormProvider({
  isDaForm,
  enableHtmlLabels,
  children,
}: DaFormProviderProps): ReactElement {
  const value = useMemo(
    () => ({ isDaForm, enableHtmlLabels }),
    [isDaForm, enableHtmlLabels]
  );

  return (
    <DaFormContext.Provider value={value}>{children}</DaFormContext.Provider>
  );
}
